import {
  Box,
  createStyles,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WildcardTagsWithExclusionTagsFilter } from '../../../generated/model/wildcardTagsWithExclusionTagsFilter';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import InputSwitch from '../../Inputs/Switch';
import { SwitchSize } from '../../Inputs/Switch/Switch';
import TagsFilterInput, { TagsFilterLabelFormatters } from '../TagsFilterInput/TagsFilterInput';

interface Props {
  root?: boolean;
  active: boolean;
  label?: string;
  field?: string;
  defaultValue?: WildcardTagsWithExclusionTagsFilter;
  onChange: (value: WildcardTagsWithExclusionTagsFilter) => void;
  labelFormatter?: TagsFilterLabelFormatters;
}

const TagsWithExclusionFilterInput: React.FC<Props> = React.memo(
  ({
    label,
    field,
    defaultValue = {
      value: {
        tags: { excludeValues: [], includeValues: [] },
      },
    },
    onChange,
    root,
    labelFormatter,
    active,
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState<WildcardTagsWithExclusionTagsFilter>(defaultValue);
    const [toggleOpenState, setToggleOpenState] = useState<boolean>(false);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    const rs: any[] = [];
    if (state.exactSearch) {
      rs.push({ label: 'Included:', value: 'Exact matches only' });
    }
    if (
      (state.value?.tags?.includeValues && state.value.tags.includeValues.length) ||
      (state.value?.tags && !Array.isArray(state.value.tags.includeValues))
    ) {
      rs.push({ label: 'Included:', value: (state.value.tags.includeValues ?? []).join(', ') });
    }
    if (
      (state.value?.tags?.excludeValues && state.value.tags.excludeValues.length) ||
      (state.value?.tags && !Array.isArray(state.value.tags.excludeValues))
    ) {
      rs.push({ label: 'NOT included:', value: (state.value.tags.excludeValues ?? []).join(', ') });
    }

    useEffect(() => {
      updateToggle(toggleOpenState);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const updateToggle = (value: boolean) => {
      if (state.value && state.value.tags && state.value.tags.excludeValues?.length) {
        setToggleOpenState(true);
      } else {
        setToggleOpenState(value);
      }
    };

    return (
      <>
        {active ? (
          <>
            <FormControl className="form-control">
              {label ? <InputLabel htmlFor={field}>{label}</InputLabel> : null}
              <Box mb={4}>
                <Grid container direction="row">
                  <Grid item className={classes.gridText}>
                    <Typography className={classes.typography}>
                      {t('companies:filterInputs.exactSearch')}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <InputSwitch
                      fieldName=""
                      checked={state.exactSearch}
                      onChange={(value) => setState({ ...state, exactSearch: value })}
                      size={SwitchSize.small}
                    />
                  </Grid>
                </Grid>
              </Box>
              <TagsFilterInput
                active={active}
                field="includeValues"
                defaultValue={{
                  value: state.value && state.value.tags ? state.value.tags.includeValues : [],
                }}
                onChange={(tags) => {
                  setState({
                    ...state,
                    value: {
                      tags: {
                        includeValues: tags.value,
                        excludeValues:
                          state.value && state.value.tags ? state.value.tags.excludeValues : [],
                      },
                    },
                  });
                }}
                labelFormatter={labelFormatter}
              />
              <div className={classes.tagToggler} onClick={() => updateToggle(!toggleOpenState)}>
                <FormHelperText>NOT included</FormHelperText>
                {toggleOpenState ? <ArrowDropUp /> : <ArrowDropDown />}
              </div>
              {toggleOpenState ? (
                <TagsFilterInput
                  field="excludeValues"
                  active={active}
                  defaultValue={{
                    value: state.value && state.value.tags ? state.value.tags.excludeValues : [],
                  }}
                  onChange={(tags) => {
                    setState({
                      ...state,
                      value: {
                        tags: {
                          excludeValues: tags.value,
                          includeValues:
                            state.value && state.value.tags ? state.value.tags.includeValues : [],
                        },
                      },
                    });
                  }}
                  labelFormatter={labelFormatter}
                />
              ) : null}
            </FormControl>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) =>
  createStyles({
    tagToggler: {
      display: 'flex',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: theme.typography.fontSize - 2,
    },
    gridText: {
      width: 'calc(100% - 48px)',
      display: 'flex',
      alignItems: 'center',
    },
    typography: {
      fontSize: theme.typography.fontSize - 2,
    },
  }),
);

export default TagsWithExclusionFilterInput;
